// Combobox — generic searchable/typeahead select. Distinct from Select:
// keyboard-navigable listbox with a client-side label filter by default and
// an async escape hatch (onQueryChange) where the caller owns filtering.
import { useId, useMemo, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icon.js';
import { useFormRowId } from './FormRow.js';

export interface ComboboxOption {
  value: string;
  label: string;
}

export interface ComboboxProps {
  options: ComboboxOption[];
  value: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  ariaLabel?: string;
  /**
   * Async escape hatch: when provided, the CALLER owns filtering — `options`
   * is rendered as-is and this fires on every query change (debounce upstream).
   * When omitted, the Combobox filters `options` by case-insensitive label
   * substring against the typed query.
   */
  onQueryChange?: (query: string) => void;
  style?: CSSProperties;
}

export function Combobox({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  loading = false,
  ariaLabel,
  onQueryChange,
  style,
}: ComboboxProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [focus, setFocus] = useState(false);
  const rowId = useFormRowId();
  const listboxId = useId();

  const selectedLabel = options.find((o) => o.value === value)?.label ?? '';

  const filtered = useMemo(() => {
    if (onQueryChange) return options;
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [onQueryChange, options, query]);

  function openMenu() {
    if (disabled) return;
    setOpen(true);
    setQuery('');
    setActiveIndex(0);
    onQueryChange?.('');
  }

  function selectAt(index: number) {
    const opt = filtered[index];
    if (!opt) return;
    onChange(opt.value);
    setOpen(false);
    setQuery('');
  }

  return (
    <div style={{ position: 'relative', ...style }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--s-2)',
          height: 'var(--control-h)',
          padding: '0 var(--s-3)',
          borderRadius: 'var(--r-2)',
          border: `1px solid ${focus ? 'var(--accent-1)' : 'var(--border-1)'}`,
          boxShadow: focus ? '0 0 0 3px var(--accent-ring)' : 'none',
          background: 'var(--bg-1)',
          transition:
            'border-color var(--dur-1) var(--ease-out), box-shadow var(--dur-1) var(--ease-out)',
        }}
      >
        <input
          id={rowId}
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            open && filtered[activeIndex] ? `${listboxId}-opt-${activeIndex}` : undefined
          }
          aria-label={ariaLabel}
          disabled={disabled}
          placeholder={placeholder}
          value={open ? query : selectedLabel}
          onChange={(e) => {
            setOpen(true);
            setQuery(e.target.value);
            setActiveIndex(0);
            onQueryChange?.(e.target.value);
          }}
          onFocus={() => {
            setFocus(true);
            openMenu();
          }}
          onBlur={() => {
            setFocus(false);
            setOpen(false);
            setQuery('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              if (!open) openMenu();
              else setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActiveIndex((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter') {
              if (open) {
                e.preventDefault();
                selectAt(activeIndex);
              }
            } else if (e.key === 'Escape') {
              if (open) {
                e.preventDefault();
                setOpen(false);
                setQuery('');
              }
            }
          }}
          style={{
            border: 0,
            outline: 0,
            background: 'transparent',
            width: '100%',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--fz-13)',
            color: 'var(--fg-1)',
          }}
        />
        <Icon
          name="chevron-down"
          size={14}
          style={{
            color: 'var(--fg-3)',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform var(--dur-1) var(--ease-out)',
          }}
        />
      </div>
      {open && (
        <ul
          role="listbox"
          id={listboxId}
          aria-busy={loading || undefined}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            listStyle: 'none',
            margin: 0,
            marginTop: 'var(--s-2)',
            padding: 'var(--s-2)',
            background: 'var(--bg-1)',
            border: '1px solid var(--border-1)',
            borderRadius: 'var(--r-2)',
            boxShadow: 'var(--shadow-2)',
            maxHeight: 240,
            overflowY: 'auto',
            zIndex: 20,
          }}
        >
          {filtered.length === 0 ? (
            loading ? null : (
              <li
                role="presentation"
                style={{
                  padding: 'var(--s-3) var(--s-4)',
                  color: 'var(--fg-3)',
                  fontSize: 'var(--fz-13)',
                }}
              >
                {t('combobox.noResults')}
              </li>
            )
          ) : (
            filtered.map((o, i) => (
              // WHY: combobox keyboard lives on the input via aria-activedescendant; options are pointer-activated. Mirrors DataTable.mobile.tsx precedent.
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events
              <li
                key={o.value}
                id={`${listboxId}-opt-${i}`}
                role="option"
                aria-selected={o.value === value}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectAt(i)}
                onMouseEnter={() => setActiveIndex(i)}
                style={{
                  padding: 'var(--s-3) var(--s-4)',
                  borderRadius: 'var(--r-2)',
                  cursor: 'pointer',
                  fontSize: 'var(--fz-13)',
                  color: 'var(--fg-1)',
                  background: i === activeIndex ? 'var(--bg-2)' : 'transparent',
                }}
              >
                {o.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
